// StickySummary — floating enquiry recap that appears on any page
// where the user has started building a Grup Xaluca enquiry. Reads
// from the BookingContext (single source of truth) and stays in sync
// in real time. Renders nothing when the enquiry is empty.
//
// View modes (toggleable by the user, persisted to sessionStorage):
//   • "expanded"  — full recap (default on first visit)
//   • "collapsed" — compact one-line summary on desktop, header bar
//                   only on mobile (key counts + Send button)
//   • "hidden"    — panel removed; a small "Show enquiry" floating
//                   pill stays in the corner so the user can bring it
//                   back without losing context

import React, { useEffect, useState } from "react";
import {
  Sparkles, Send, Loader2, ChevronUp, ChevronDown, X, Minus, Maximize2, ArrowRight,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useBooking } from "@/contexts/BookingContext";
import { HOTELS } from "@/lib/data";
import { isBivouac } from "@/lib/propertyType";
import { pickBoardLabel } from "@/lib/boardBasis";
import { getCampLabel } from "@/lib/bookingCamps";
import PropertyThumb from "@/components/PropertyThumb";

// Editorial service catalogue used by the summary to resolve service
// titles. Kept here (rather than importing from BookingPage) so the
// summary can render correctly even when /booking is not mounted.
import { SERVICE_CATALOGUE } from "@/lib/serviceCatalogue";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const L = {
  your_enquiry: { en: "Your enquiry", fr: "Votre demande", es: "Su solicitud" },
  rooms: { en: "Rooms", fr: "Chambres", es: "Hab." },
  tents: { en: "Tents", fr: "Tentes", es: "Tiendas" },
  adults: { en: "Adults", fr: "Adultes", es: "Adultos" },
  children: { en: "Children", fr: "Enfants", es: "Niños" },
  no_dates: { en: "Dates to confirm", fr: "Dates à confirmer", es: "Fechas por confirmar" },
  people: { en: "guests", fr: "voyageurs", es: "huéspedes" },
  submit: { en: "Send enquiry", fr: "Envoyer la demande", es: "Enviar solicitud" },
  sending: { en: "Sending…", fr: "Envoi…", es: "Enviando…" },
  open: { en: "Open enquiry summary", fr: "Ouvrir le récapitulatif", es: "Abrir resumen" },
  close: { en: "Close enquiry summary", fr: "Fermer le récapitulatif", es: "Cerrar resumen" },
  items: { en: "items", fr: "éléments", es: "elementos" },

  // ── Stepwise CTA labels — kept identical to those used inside the
  // booking widget so the floating summary's primary action button
  // always mirrors the widget's current step. The button label
  // updates the instant the user moves between steps because both
  // surfaces read `flowStep` from the shared BookingContext.
  cta_continue_extras: { en: "Continue to extras", fr: "Continuer vers les extras", es: "Continuar a los extras" },
  cta_continue_details: { en: "Continue to details", fr: "Continuer vers les détails", es: "Continuar a los detalles" },

  // ── View-mode toggle labels ─────────────────────────────────────
  collapse: { en: "Collapse summary", fr: "Réduire le récapitulatif", es: "Contraer el resumen" },
  expand: { en: "Expand summary", fr: "Agrandir le récapitulatif", es: "Ampliar el resumen" },
  hide: { en: "Hide summary", fr: "Masquer le récapitulatif", es: "Ocultar el resumen" },
  show_summary: { en: "Show enquiry", fr: "Voir la demande", es: "Ver solicitud" },
  clear_all: { en: "Clear all", fr: "Tout effacer", es: "Borrar todo" },

  // ── Compact one-liner labels ────────────────────────────────────
  stay_singular: { en: "stay", fr: "séjour", es: "estancia" },
  stay_plural: { en: "stays", fr: "séjours", es: "estancias" },
  extra_singular: { en: "extra", fr: "extra", es: "extra" },
  extra_plural: { en: "extras", fr: "extras", es: "extras" },
};

// ── Session-storage helpers ───────────────────────────────────────
// The user's chosen view mode is remembered for the duration of the
// browser session only (per spec: "Remember the user's preferred
// summary state during the current session"). sessionStorage clears on
// tab close — exactly the right scope for an ephemeral UI preference.
const VIEW_MODE_KEY = "xaluca.summaryView.v1";
const VIEW_MODES = ["expanded", "collapsed", "hidden"];

const readStoredMode = () => {
  if (typeof window === "undefined") return "expanded";
  try {
    const v = window.sessionStorage.getItem(VIEW_MODE_KEY);
    return VIEW_MODES.includes(v) ? v : "expanded";
  } catch {
    return "expanded";
  }
};

const writeStoredMode = (mode) => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(VIEW_MODE_KEY, mode);
  } catch {
    // sessionStorage quota / privacy mode failures are non-fatal.
  }
};

// One-line natural-language summary used by the collapsed mode (and as
// the label on the reopen pill when hidden). Returns localised text
// like "2 stays · 1 extra".
const buildCompactSummary = (hotelCount, extraCount, lang) => {
  const parts = [];
  if (hotelCount > 0) {
    parts.push(`${hotelCount} ${pick(hotelCount === 1 ? L.stay_singular : L.stay_plural, lang)}`);
  }
  if (extraCount > 0) {
    parts.push(`${extraCount} ${pick(extraCount === 1 ? L.extra_singular : L.extra_plural, lang)}`);
  }
  return parts.join(" · ");
};

// ── StepDots ──────────────────────────────────────────────────────
// Glanceable 3-dot progress cue, mirroring the booking widget's
// `flowStep` (1 = Accommodation, 2 = Extras, 3 = Details). Especially
// useful in the collapsed view where the CTA label is the only other
// indication of where the user is in the funnel. Render at ~28 px
// wide so it fits cleanly next to the overline.
//   • completed step → muted sand fill (#D4A373 / 60% alpha)
//   • current step   → terracotta filled + faint outer ring
//   • upcoming step  → outlined only, 30 % alpha
const StepLabels = {
  1: { en: "Accommodation", fr: "Hébergement", es: "Alojamiento" },
  2: { en: "Extras", fr: "Extras", es: "Extras" },
  3: { en: "Details", fr: "Coordonnées", es: "Datos" },
};
const StepDots = ({ flowStep, lang, testId = "booking-sticky-summary-stepdots" }) => {
  const total = 3;
  return (
    <span
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={flowStep}
      aria-label={`Step ${flowStep} of ${total} — ${pick(StepLabels[flowStep] || StepLabels[1], lang)}`}
      className="inline-flex items-center gap-1.5 shrink-0"
      data-testid={testId}
      data-flow-step={flowStep}
    >
      {[1, 2, 3].map((n) => {
        const isCurrent = n === flowStep;
        const isDone = n < flowStep;
        const base = "block w-1.5 h-1.5 rounded-full transition-all duration-200";
        if (isCurrent) {
          return (
            <span
              key={`sd-${n}`}
              className={`${base} bg-[#C16542] ring-2 ring-[#C16542]/25 scale-110`}
              data-testid={`${testId}-current-${n}`}
            />
          );
        }
        if (isDone) {
          return (
            <span
              key={`sd-${n}`}
              className={`${base} bg-[#D4A373]/70`}
              data-testid={`${testId}-done-${n}`}
            />
          );
        }
        return (
          <span
            key={`sd-${n}`}
            className={`${base} bg-transparent border border-[#FDFBF7]/30`}
            data-testid={`${testId}-todo-${n}`}
          />
        );
      })}
    </span>
  );
};

const fmtDate = (iso, lang) => {
  if (!iso) return null;
  const localeMap = { en: "en-GB", fr: "fr-FR", es: "es-ES" };
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(localeMap[lang] || "en-GB", { day: "2-digit", month: "short" });
  } catch {
    return iso;
  }
};

const nightsBetween = (a, b) => {
  if (!a || !b) return null;
  const diff = Math.round((new Date(b) - new Date(a)) / 86400000);
  return diff > 0 ? diff : null;
};

const nightsLabel = (n, lang) => {
  if (!n) return null;
  if (lang === "fr") return n === 1 ? "1 nuit" : `${n} nuits`;
  if (lang === "es") return n === 1 ? "1 noche" : `${n} noches`;
  return n === 1 ? "1 night" : `${n} nights`;
};

// ── Body content (shared between desktop panel + mobile drawer) ─────
const SummaryBody = ({
  hotelEntries,
  serviceEntries,
  lang,
  isSubmitting,
  onAction,
  actionLabel,
  actionIcon: ActionIcon = Send,
  actionDisabled = false,
  actionTestId = "booking-sticky-submit",
}) => (
  <>
    <ul className="space-y-3 max-h-[55vh] overflow-y-auto pr-1 [scrollbar-width:thin]">
      {hotelEntries.map(({ slug, detail, hotel, rowKey, campLabel }) => {
        if (!hotel) return null;
        const bivouac = isBivouac(slug);
        const checkIn = fmtDate(detail.check_in, lang);
        const checkOut = fmtDate(detail.check_out, lang);
        const nights = nightsLabel(nightsBetween(detail.check_in, detail.check_out), lang);
        const propertyRooms = Array.isArray(detail.rooms) ? detail.rooms : [];
        const unitLabel = bivouac ? pick(L.tents, lang) : pick(L.rooms, lang);
        return (
          <li
            key={`sum-h-${rowKey}`}
            className="border-t border-[#D4A373]/15 pt-3 first:border-t-0 first:pt-0"
            data-testid={`booking-summary-hotel-${rowKey}`}
          >
            <div className="flex items-start gap-2.5">
              <PropertyThumb
                slug={slug}
                alt={hotel.name}
                aspectRatio="4/3"
                className="w-20 h-16 shrink-0 mt-0.5 border border-[#D4A373]/25"
                testId={`booking-summary-hotel-${rowKey}-thumb`}
                cmsSlot={`booking-widget.thumb.${slug}`}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug text-[#FDFBF7] truncate">{hotel.name}</p>
                {campLabel ? (
                  <p
                    className="text-[10px] tracking-[0.22em] uppercase text-[#D4A373] mt-0.5 truncate"
                    data-testid={`booking-summary-hotel-${rowKey}-camp`}
                  >
                    {campLabel}
                  </p>
                ) : null}
                {hotel.location ? (
                  <p className="text-[10px] tracking-[0.18em] uppercase text-[#FDFBF7]/55 mt-0.5 truncate">
                    {pick(hotel.location, lang)}
                  </p>
                ) : null}
                <p className="mt-2 text-[11px] text-[#FDFBF7]/85">
                  {checkIn && checkOut ? (
                    <>
                      {checkIn} <span className="text-[#D4A373]">→</span> {checkOut}
                      {nights ? <span className="text-[#FDFBF7]/55"> · {nights}</span> : null}
                    </>
                  ) : (
                    <span className="text-[#FDFBF7]/55 italic">{pick(L.no_dates, lang)}</span>
                  )}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {propertyRooms.map((room, idx) => {
                    const adults = Number(room.adults) || 0;
                    const childrenCount = Number(room.children) || 0;
                    const infants = Number(room.infants) || 0;
                    const board = room.board_basis
                      ? pickBoardLabel(room.board_basis, lang, { bivouac })
                      : null;
                    return (
                      <li
                        key={`sum-h-${rowKey}-r-${idx}`}
                        className="pl-2 border-l border-[#D4A373]/30"
                      >
                        <p className="text-[10px] tracking-[0.22em] uppercase text-[#D4A373]/90">
                          {unitLabel} {String(idx + 1).padStart(2, "0")}
                        </p>
                        {room.room_type ? (
                          <p className="text-[11px] text-[#FDFBF7]/85 mt-0.5">{room.room_type}</p>
                        ) : null}
                        <p className="text-[11px] text-[#FDFBF7]/65 mt-0.5">
                          {adults} {pick(L.adults, lang).toLowerCase()}
                          {childrenCount > 0 ? (
                            <> · {childrenCount} {pick(L.children, lang).toLowerCase()}</>
                          ) : null}
                          {infants > 0 ? (
                            <>
                              {" · "}
                              {infants}{" "}
                              {pick({ en: "infants", fr: "bébés", es: "bebés" }, lang)}
                            </>
                          ) : null}
                          {board ? (
                            <>
                              {" "}
                              <span className="text-[#D4A373]">·</span> {board}
                            </>
                          ) : null}
                        </p>
                      </li>
                    );
                  })}
                </ul>
                {detail.notes ? (
                  <p
                    className="mt-2 text-[10px] leading-relaxed text-[#FDFBF7]/55 line-clamp-2 italic"
                    title={detail.notes}
                  >
                    “{detail.notes}”
                  </p>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}

      {serviceEntries.map(({ key, detail, service }) => {
        if (!service) return null;
        const dateStr = fmtDate(detail.date, lang);
        const people = Number(detail.people) || 0;
        return (
          <li
            key={`sum-s-${key}`}
            className="border-t border-[#D4A373]/15 pt-3 first:border-t-0 first:pt-0"
            data-testid={`booking-summary-service-${key}`}
          >
            <div className="flex items-start gap-2">
              <Sparkles
                className="w-3.5 h-3.5 text-[#D4A373] mt-0.5 shrink-0"
                strokeWidth={1.5}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm leading-snug text-[#FDFBF7] truncate">
                  {pick(service.title, lang)}
                </p>
                <p className="mt-2 text-[11px] text-[#FDFBF7]/85">
                  {dateStr || (
                    <span className="text-[#FDFBF7]/55 italic">{pick(L.no_dates, lang)}</span>
                  )}
                  {detail.time ? <span className="text-[#FDFBF7]/55"> · {detail.time}</span> : null}
                </p>
                {people > 0 ? (
                  <p className="mt-1 text-[11px] text-[#FDFBF7]/85">
                    {people} {pick(L.people, lang)}
                    {detail.duration ? (
                      <span className="text-[#FDFBF7]/55"> · {detail.duration}</span>
                    ) : null}
                  </p>
                ) : null}
                {detail.notes ? (
                  <p
                    className="mt-2 text-[10px] leading-relaxed text-[#FDFBF7]/55 line-clamp-2 italic"
                    title={detail.notes}
                  >
                    “{detail.notes}”
                  </p>
                ) : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>

    <button
      type="button"
      onClick={onAction}
      disabled={isSubmitting || actionDisabled}
      className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 disabled:cursor-not-allowed text-[#FDFBF7] px-5 py-3 text-[10px] tracking-[0.3em] uppercase transition-colors"
      data-testid={actionTestId}
    >
      {isSubmitting ? (
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
      ) : (
        <ActionIcon className="w-3.5 h-3.5" strokeWidth={1.6} />
      )}
      {isSubmitting ? pick(L.sending, lang) : actionLabel}
    </button>
  </>
);

// Surfaces where the floating summary should NEVER appear (admin / CMS
// editors etc.). Any route starting with one of these is suppressed.
const HIDDEN_ROUTE_PREFIXES = ["/admin"];

const StickySummary = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    selectedHotels,
    selectedServices,
    selectedHotelSlugs,
    selectedServiceKeys,
    isSubmitting,
    submit,
    flowStep,
    setFlowStep,
    requestClearAll,
  } = useBooking();

  // ── View mode (expanded | collapsed) ────────────────────────────
  // The summary stays a permanent part of the booking experience —
  // the previous "hidden" mode + reopen pill were removed when the
  // X icon was repurposed as "Clear all". Users still get a quiet
  // mode via the collapse toggle.
  const [viewMode, setViewMode] = useState(() => {
    const v = readStoredMode();
    return v === "collapsed" ? "collapsed" : "expanded";
  });
  useEffect(() => {
    writeStoredMode(viewMode);
  }, [viewMode]);

  // Mobile drawer open state — separate from `viewMode` because the
  // mobile bar has its own expand/collapse mechanic via tap-toggle.
  const [mobileOpen, setMobileOpen] = useState(false);

  const hotelEntries = selectedHotelSlugs.flatMap((slug) => {
    const raw = selectedHotels[slug] || {};
    const hotel = HOTELS.find((h) => h.slug === slug) || null;
    // Camp-parent property with per-camp bookings — emit one entry
    // per camp so each camp renders as its own summary card with its
    // own dates / rooms / notes.
    if (Array.isArray(raw.campBookings) && raw.campBookings.length > 0) {
      return raw.campBookings.map((cb, idx) => ({
        slug,
        rowKey: `${slug}__${cb?.campSlug || "camp"}__${idx}`,
        campSlug: cb?.campSlug || null,
        campLabel: cb?.campSlug ? getCampLabel(slug, cb.campSlug, lang) : "",
        detail: {
          check_in: cb?.check_in || "",
          check_out: cb?.check_out || "",
          notes: cb?.notes || "",
          rooms: Array.isArray(cb?.rooms) ? cb.rooms : [],
        },
        hotel,
      }));
    }
    return [{
      slug,
      rowKey: slug,
      campSlug: null,
      campLabel: "",
      detail: raw,
      hotel,
    }];
  });
  const serviceEntries = selectedServiceKeys.map((key) => ({
    key,
    detail: selectedServices[key] || {},
    service: SERVICE_CATALOGUE.find((s) => s.key === key) || null,
  }));

  const totalCount = hotelEntries.length + selectedServiceKeys.length;
  const hiddenForRoute = HIDDEN_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p));
  const count = totalCount;
  if (hiddenForRoute || count === 0) return null;

  const compactLine = buildCompactSummary(
    hotelEntries.length,
    selectedServiceKeys.length,
    lang
  );

  // ── Stepwise action (mirrors the widget's CTA exactly) ──────────
  // Reads `flowStep` from the shared BookingContext so the floating
  // summary's button always matches whatever the widget is showing.
  // Click behaviour:
  //   Step 1 → setFlowStep(2)   ("Continue to extras")
  //   Step 2 → setFlowStep(3)   ("Continue to details")
  //   Step 3 → submit()         ("Send inquiry") — same validation as
  //                              the widget; on error the widget
  //                              focuses the first invalid field (its
  //                              effect listens to `fieldErrors`).
  //
  // If we're NOT on a page that hosts the booking widget (e.g. a
  // /hotels/:slug page), the step-1 / step-2 advance navigates the
  // user to /booking so they can complete the flow there.
  const widgetMounted = typeof window !== "undefined" && !!document.querySelector("[data-testid='home-booking-widget']");

  const handleSummaryAction = async () => {
    if (flowStep === 3) {
      // SUBMIT — validation lives in the shared context.submit().
      // The widget's effect on `fieldErrors` will scroll/focus the
      // first invalid input automatically.
      if (!widgetMounted && location.pathname !== "/booking") {
        navigate("/booking#send");
        return;
      }
      await submit({ source: "sticky-summary" });
      return;
    }
    // STEP ADVANCE — must have at least one selection before
    // progressing past Step 1 (the widget's "Continue to extras"
    // button enforces the same rule).
    if (flowStep === 1 && selectedHotelSlugs.length === 0 && selectedServiceKeys.length === 0) {
      return;
    }
    if (!widgetMounted && location.pathname !== "/booking") {
      navigate("/booking");
      // Defer the step bump so the route change settles first.
      window.setTimeout(() => setFlowStep(flowStep + 1), 50);
      return;
    }
    setFlowStep(flowStep + 1);
  };

  // Map `flowStep` → (label, icon) so the button copy and glyph
  // update the instant the user (or the widget) moves between steps.
  const actionLabel = (() => {
    if (flowStep === 1) return pick(L.cta_continue_extras, lang);
    if (flowStep === 2) return pick(L.cta_continue_details, lang);
    return pick(L.submit, lang); // Step 3
  })();
  const actionIcon = flowStep === 3 ? Send : ArrowRight;
  const actionTestId = `booking-sticky-action-step-${flowStep}`;
  // Step 1 needs at least one selected item before the user can
  // progress (matches the widget's `disabled={!hasSelections}` rule).
  const actionDisabled =
    flowStep === 1 && selectedHotelSlugs.length === 0 && selectedServiceKeys.length === 0;

  // The X icon in the summary header is now repurposed as Clear all.
  // It calls into `requestClearAll()` on the shared BookingContext,
  // which opens the same <ClearAllConfirmDialog /> the widget's own
  // Clear-All button uses.
  return (
    <>
      {/* ── Desktop / large-screen floating panel ─────────────── */}
      <aside
        className={`hidden xl:block fixed bottom-6 right-6 z-30 bg-[#1A1513] text-[#FDFBF7] border border-[#D4A373]/40 shadow-2xl transition-all duration-300 ${
          viewMode === "collapsed" ? "w-[300px]" : "w-[360px]"
        }`}
        data-testid="booking-sticky-summary"
        data-view-mode={viewMode}
      >
        <div className="p-5">
          {/* Header — overline + step dots + view-mode toggle controls.
              The dots give the user a glanceable progress cue without
              needing to expand the panel. */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4A373] truncate">
                {pick(L.your_enquiry, lang)} · {String(count).padStart(2, "0")}
              </p>
              <StepDots flowStep={flowStep} lang={lang} />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() =>
                  setViewMode((m) => (m === "collapsed" ? "expanded" : "collapsed"))
                }
                className="p-1.5 text-[#FDFBF7]/60 hover:text-[#D4A373] transition-colors"
                aria-label={pick(viewMode === "collapsed" ? L.expand : L.collapse, lang)}
                title={pick(viewMode === "collapsed" ? L.expand : L.collapse, lang)}
                data-testid="booking-sticky-summary-collapse-toggle"
              >
                {viewMode === "collapsed" ? (
                  <Maximize2 className="w-3.5 h-3.5" strokeWidth={1.7} />
                ) : (
                  <Minus className="w-3.5 h-3.5" strokeWidth={1.7} />
                )}
              </button>
              <button
                type="button"
                onClick={requestClearAll}
                className="p-1.5 text-[#FDFBF7]/60 hover:text-[#A35133] transition-colors"
                aria-label={pick(L.clear_all, lang)}
                title={pick(L.clear_all, lang)}
                data-testid="booking-sticky-summary-clear-all"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.7} />
              </button>
            </div>
          </div>

          {/* Body — full list when expanded, compact 1-liner +
              step-aware action when collapsed. */}
          {viewMode === "expanded" ? (
            <div className="mt-3" data-testid="booking-sticky-summary-body">
              <SummaryBody
                hotelEntries={hotelEntries}
                serviceEntries={serviceEntries}
                lang={lang}
                isSubmitting={isSubmitting}
                onAction={handleSummaryAction}
                actionLabel={actionLabel}
                actionIcon={actionIcon}
                actionDisabled={actionDisabled}
                actionTestId={actionTestId}
              />
            </div>
          ) : (
            <div className="mt-3" data-testid="booking-sticky-summary-compact">
              <p className="text-[12px] text-[#FDFBF7]/80 leading-snug">{compactLine}</p>
              <button
                type="button"
                onClick={handleSummaryAction}
                disabled={isSubmitting || actionDisabled}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-50 disabled:cursor-not-allowed text-[#FDFBF7] px-4 py-2.5 text-[10px] tracking-[0.3em] uppercase transition-colors"
                data-testid={`${actionTestId}-compact`}
              >
                {isSubmitting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : flowStep === 3 ? (
                  <Send className="w-3.5 h-3.5" strokeWidth={1.6} />
                ) : (
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                )}
                {isSubmitting ? pick(L.sending, lang) : actionLabel}
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ── Mobile / tablet bottom sticky bar (below xl) ─────── */}
      <div
        className="xl:hidden fixed inset-x-0 bottom-0 z-30"
        data-testid="booking-sticky-summary-mobile"
        data-view-mode={viewMode}
      >
        {/* Backdrop when drawer is open */}
        {mobileOpen ? (
          <button
            type="button"
            aria-label={pick(L.close, lang)}
            className="fixed inset-0 bg-[#1A1513]/60"
            onClick={() => setMobileOpen(false)}
            data-testid="booking-sticky-summary-backdrop"
          />
        ) : null}

        {/* Drawer panel (slides up when open) */}
        <div className="relative bg-[#1A1513] text-[#FDFBF7] border-t border-[#D4A373]/40 shadow-2xl">
          {/* Header bar — count chip + summary line + toggle controls */}
          <div className="flex items-center gap-2 px-3 py-3 sm:px-5">
            <button
              type="button"
              className="flex-1 min-w-0 flex items-center gap-3 text-left"
              onClick={() => setMobileOpen((v) => !v)}
              aria-expanded={mobileOpen}
              data-testid="booking-sticky-summary-toggle"
            >
              <span className="inline-flex items-center justify-center w-9 h-9 bg-[#C16542] text-[#FDFBF7] rounded-full text-[12px] tracking-[0.15em] font-semibold shrink-0">
                {String(count).padStart(2, "0")}
              </span>
              <div className="flex flex-col items-start min-w-0">
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373]">
                  {pick(L.your_enquiry, lang)}
                  <StepDots
                    flowStep={flowStep}
                    lang={lang}
                    testId="booking-sticky-summary-stepdots-mobile"
                  />
                </span>
                <span className="text-[11px] text-[#FDFBF7]/70 truncate">
                  {compactLine || `${count} ${pick(L.items, lang)}`}
                </span>
              </div>
              {mobileOpen ? (
                <ChevronDown className="w-4 h-4 text-[#D4A373] shrink-0 ml-1" strokeWidth={1.6} />
              ) : (
                <ChevronUp className="w-4 h-4 text-[#D4A373] shrink-0 ml-1" strokeWidth={1.6} />
              )}
            </button>
            {/* Clear-All button — always available on the right of
                the bar. Calls the shared requestClearAll() so the
                same confirmation dialog opens whether the user is on
                desktop, mobile, or the embedded /booking widget. */}
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                requestClearAll();
              }}
              className="p-2 text-[#FDFBF7]/60 hover:text-[#A35133] transition-colors shrink-0"
              aria-label={pick(L.clear_all, lang)}
              title={pick(L.clear_all, lang)}
              data-testid="booking-sticky-summary-clear-all-mobile"
            >
              <X className="w-4 h-4" strokeWidth={1.7} />
            </button>
          </div>

          {/* Expanded drawer body */}
          {mobileOpen ? (
            <div className="px-5 pb-5 border-t border-[#D4A373]/15 pt-4">
              <SummaryBody
                hotelEntries={hotelEntries}
                serviceEntries={serviceEntries}
                lang={lang}
                isSubmitting={isSubmitting}
                onAction={handleSummaryAction}
                actionLabel={actionLabel}
                actionIcon={actionIcon}
                actionDisabled={actionDisabled}
                actionTestId={`${actionTestId}-mobile`}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default StickySummary;
